"use client";

import { useEffect, useMemo, useState } from "react";
import { Carousel } from "@mantine/carousel";
import { Button } from "@mantine/core";
import styles from "./CollagePicturesWithDescription.module.scss";
import { AppButton } from "@/src/components/AppButton/AppButton";

type Mode = "all" | "anniversaire";

export type CollagePicturesWithDescriptionSlice = {
    type: "collage-pictures-with-description";
    title?: string;
    subtitle?: string;
    description?: string;
    mode: Mode;
    ctaLabel?: string;
    ctaHref?: string;
    themeHex?: string;
};

type BookingProduct = {
    _id?: string;
    id?: string;
    slug?: string;
    label?: string;
    title?: string;
    name?: string;
    description?: string;
    shortDescription?: string;
    publicDescription?: string;
    imageUrl?: string;
    image?: string;
    coverImage?: string;
    bannerImage?: string;
    ageMin?: number;
    durationMin?: number;
    enabled?: boolean;
    active?: boolean;
};

type RdvActivity = {
    _id?: string;
    id?: string;
    slug?: string;
    label?: string;
    title?: string;
    name?: string;
    description?: string;
    shortDescription?: string;
    imageUrl?: string;
    image?: string;
    coverImage?: string;
    bannerImage?: string;
    minAge?: number;
    maxAge?: number;
    ageLabel?: string;
    durationMin?: number;
    enabled?: boolean;
    active?: boolean;
};

type PackScopedPricing = {
    packKey: string;
    enabled: boolean;
    priceCents: number;
};

type RdvActivityPricing = {
    activityId: string;
    enabled: boolean;
    pricingByPack?: PackScopedPricing[];
};

type BookingProductPricing = {
    productId: string;
    enabled: boolean;
    pricingByPack?: PackScopedPricing[];
};

type AnniversairePricingConfig = {
    _id?: string;
    rdvActivitiesPricing?: RdvActivityPricing[];
    bookingProductsPricing?: BookingProductPricing[];
};

type AnniversairePricingApiResponse = {
    items?: AnniversairePricingConfig[];
    total?: number;
    page?: number;
    perPage?: number;
};

type DisplayItem = {
    id: string;
    label: string;
    description: string;
    imageUrl: string;
    href?: string;
    durationMin?: number;
    ageText?: string;
    source: "booking_product" | "rdv_activity";
};

function asArray<T>(value: unknown): T[] {
    return Array.isArray(value) ? (value as T[]) : [];
}

function getApiList<T>(raw: any): T[] {
    if (Array.isArray(raw)) return raw as T[];
    if (Array.isArray(raw?.items)) return raw.items as T[];
    if (Array.isArray(raw?.data)) return raw.data as T[];
    if (Array.isArray(raw?.results)) return raw.results as T[];
    return [];
}

function cleanText(value?: string): string {
    return String(value || "").replace(/\s+/g, " ").trim();
}

function pickLabel(item: Record<string, any>): string {
    return (
        cleanText(item.label) ||
        cleanText(item.title) ||
        cleanText(item.name) ||
        "Activité créative"
    );
}

function pickDescription(item: Record<string, any>, fallback: string): string {
    return (
        cleanText(item.description) ||
        cleanText(item.shortDescription) ||
        cleanText(item.publicDescription) ||
        fallback
    );
}

function pickImage(item: Record<string, any>): string {
    return (
        item.imageUrl ||
        item.image ||
        item.coverImage ||
        item.bannerImage ||
        "https://ik.imagekit.io/usertwohestia/ateliers-mini-monkeys-logo-cropped.webp"
    );
}

function bookingHref(item: BookingProduct): string | undefined {
    return item.slug ? `/activities#${item.slug}` : undefined;
}

function rdvHref(item: RdvActivity): string | undefined {
    return item.slug ? `/rdv-activities#${item.slug}` : undefined;
}

function toBookingDisplay(item: BookingProduct): DisplayItem {
    const ref = item as Record<string, any>;
    const ageMin = typeof item.ageMin === "number" ? item.ageMin : undefined;

    return {
        id: String(item._id || item.id || item.slug || Math.random()),
        label: pickLabel(ref),
        description: pickDescription(
            ref,
            "Un atelier créatif à découvrir chez MiniMonkeys."
        ),
        imageUrl: pickImage(ref),
        href: bookingHref(item),
        durationMin:
            typeof item.durationMin === "number" ? item.durationMin : undefined,
        ageText: ageMin ? `Dès ${ageMin} ans` : undefined,
        source: "booking_product",
    };
}

function toRdvDisplay(item: RdvActivity): DisplayItem {
    const ref = item as Record<string, any>;

    let ageText = "";
    if (item.ageLabel) ageText = item.ageLabel;
    else if (typeof item.minAge === "number" && typeof item.maxAge === "number") {
        ageText = `${item.minAge}-${item.maxAge} ans`;
    } else if (typeof item.minAge === "number") {
        ageText = `Dès ${item.minAge} ans`;
    }

    return {
        id: String(item._id || item.id || item.slug || Math.random()),
        label: pickLabel(ref),
        description: pickDescription(
            ref,
            "Une activité créative proposée dans les rendez-vous MiniMonkeys."
        ),
        imageUrl: pickImage(ref),
        href: rdvHref(item),
        durationMin:
            typeof item.durationMin === "number" ? item.durationMin : undefined,
        ageText: ageText || undefined,
        source: "rdv_activity",
    };
}

function dedupeById<T extends { id: string }>(items: T[]): T[] {
    const seen = new Set<string>();
    return items.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
    });
}

function normalizeId(value: unknown): string {
    return String(value || "").trim();
}

function getAnniversaireConfig(raw: AnniversairePricingApiResponse | any): AnniversairePricingConfig | null {
    if (raw?.items && Array.isArray(raw.items) && raw.items.length > 0) {
        return raw.items[0] || null;
    }
    if (raw?.bookingProductsPricing || raw?.rdvActivitiesPricing) {
        return raw as AnniversairePricingConfig;
    }
    return null;
}

export default function CollagePicturesWithDescription({
    title,
    subtitle,
    description,
    mode,
    ctaLabel,
    ctaHref,
    themeHex = "#ead7cf",
}: CollagePicturesWithDescriptionSlice) {
    const [bookingItems, setBookingItems] = useState<DisplayItem[]>([]);
    const [rdvItems, setRdvItems] = useState<DisplayItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError("");

            try {
                const [bookingRes, rdvRes] = await Promise.all([
                    fetch("/api/admin/crud/booking_products", { cache: "no-store" }),
                    fetch("/api/admin/crud/rdv_activities", { cache: "no-store" }),
                ]);

                if (!bookingRes.ok || !rdvRes.ok) {
                    throw new Error("Impossible de charger les activités.");
                }

                const [bookingRaw, rdvRaw] = await Promise.all([
                    bookingRes.json(),
                    rdvRes.json(),
                ]);

                const allBooking = getApiList<BookingProduct>(bookingRaw).filter(
                    (item) => item.enabled !== false && item.active !== false
                );

                const allRdv = getApiList<RdvActivity>(rdvRaw).filter(
                    (item) => item.enabled !== false && item.active !== false
                );

                console.log("[Collage] all booking_products:", allBooking);
                console.log("[Collage] all rdv_activities:", allRdv);

                if (mode === "all") {
                    if (!cancelled) {
                        setBookingItems(dedupeById(allBooking.map(toBookingDisplay)));
                        setRdvItems(dedupeById(allRdv.map(toRdvDisplay)));
                    }
                    return;
                }

                const annivRes = await fetch("/api/admin/crud/anniversaire_pricing", {
                    cache: "no-store",
                });

                if (!annivRes.ok) {
                    throw new Error("Impossible de charger la configuration anniversaire.");
                }

                const annivRaw: AnniversairePricingApiResponse = await annivRes.json();
                const annivConfig = getAnniversaireConfig(annivRaw);

                console.log("[Collage][Anniversaire] raw response:", annivRaw);
                console.log("[Collage][Anniversaire] extracted config:", annivConfig);

                if (!annivConfig) {
                    console.warn("[Collage][Anniversaire] No config found in response.");
                    if (!cancelled) {
                        setBookingItems([]);
                        setRdvItems([]);
                    }
                    return;
                }

                const enabledBookingPricing = asArray<BookingProductPricing>(
                    annivConfig.bookingProductsPricing
                ).filter((item) => item.enabled);

                const enabledRdvPricing = asArray<RdvActivityPricing>(
                    annivConfig.rdvActivitiesPricing
                ).filter((item) => item.enabled);

                const enabledBookingIds = new Set(
                    enabledBookingPricing.map((item) => normalizeId(item.productId))
                );

                const enabledRdvIds = new Set(
                    enabledRdvPricing.map((item) => normalizeId(item.activityId))
                );

                console.log(
                    "[Collage][Anniversaire] enabled booking ids:",
                    Array.from(enabledBookingIds)
                );
                console.log(
                    "[Collage][Anniversaire] enabled rdv ids:",
                    Array.from(enabledRdvIds)
                );
                console.log(
                    "[Collage][Anniversaire] booking ids available:",
                    allBooking.map((item) => ({
                        _id: normalizeId(item._id),
                        id: normalizeId(item.id),
                        label: pickLabel(item as Record<string, any>),
                    }))
                );
                console.log(
                    "[Collage][Anniversaire] rdv ids available:",
                    allRdv.map((item) => ({
                        _id: normalizeId(item._id),
                        id: normalizeId(item.id),
                        label: pickLabel(item as Record<string, any>),
                    }))
                );

                const anniversaireBooking = allBooking.filter((item) => {
                    const match =
                        enabledBookingIds.has(normalizeId(item._id)) ||
                        enabledBookingIds.has(normalizeId(item.id));
                    if (match) {
                        console.log("[Collage][Anniversaire] matched booking product:", item);
                    }
                    return match;
                });

                const anniversaireRdv = allRdv.filter((item) => {
                    const match =
                        enabledRdvIds.has(normalizeId(item._id)) ||
                        enabledRdvIds.has(normalizeId(item.id));
                    if (match) {
                        console.log("[Collage][Anniversaire] matched rdv activity:", item);
                    }
                    return match;
                });

                console.log(
                    "[Collage][Anniversaire] final matched booking count:",
                    anniversaireBooking.length
                );
                console.log(
                    "[Collage][Anniversaire] final matched rdv count:",
                    anniversaireRdv.length
                );

                if (!cancelled) {
                    setBookingItems(dedupeById(anniversaireBooking.map(toBookingDisplay)));
                    setRdvItems(dedupeById(anniversaireRdv.map(toRdvDisplay)));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Une erreur est survenue lors du chargement."
                    );
                    setBookingItems([]);
                    setRdvItems([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, [mode]);

    const totalCount = bookingItems.length + rdvItems.length;

    const styleVars = useMemo(
        () => ({ ["--collage-theme" as string]: themeHex }),
        [themeHex]
    );

    return (
        <section className={styles.section} style={styleVars}>
            <div className={styles.shell}>
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        {title ? <h2>{title}</h2> : null}
                        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
                        {description ? <p className={styles.description}>{description}</p> : null}
                    </div>

                    {ctaLabel && ctaHref ? (
                        <AppButton
                            component="a"
                            href={ctaHref}
                            radius="xl"
                        >
                            {ctaLabel}
                        </AppButton>
                    ) : null}
                </div>

                {loading ? (
                    <div className={styles.stateBox}>Chargement des activités…</div>
                ) : error ? (
                    <div className={styles.stateBox}>{error}</div>
                ) : totalCount === 0 ? (
                    <div className={styles.stateBox}>
                        Aucune activité disponible pour le moment.
                    </div>
                ) : (
                    <div className={styles.content}>
                        {bookingItems.length > 0 ? (
                            <CarouselBlock
                                title="Ateliers"
                                items={bookingItems}
                            />
                        ) : null}

                        {rdvItems.length > 0 ? (
                            <CarouselBlock
                                title="Rendez-vous créatifs"
                                items={rdvItems}
                            />
                        ) : null}
                    </div>
                )}
            </div>
        </section>
    );
}

function CarouselBlock({
    title,
    items,
}: {
    title: string;
    items: DisplayItem[];
}) {
    return (
        <div className={styles.groupBlock}>
            <div className={styles.groupHead}>
                <h3>{title}</h3>
                <span>{items.length}</span>
            </div>

            <Carousel
                withIndicators={items.length > 3}
                withControls={items.length > 3}
                slideSize={{ base: "100%", sm: "50%", lg: "33.333333%" }}
                slideGap="md"
                emblaOptions={{
                    align: "start",
                    loop: items.length > 3
                }}
                classNames={{
                    root: styles.carouselRoot,
                    viewport: styles.carouselViewport,
                    controls: styles.carouselControls,
                    control: styles.carouselControl,
                    indicators: styles.carouselIndicators,
                    indicator: styles.carouselIndicator,
                }}
            >
                {items.map((activity) => (
                    <Carousel.Slide key={activity.id}>
                        <ActivityCard activity={activity} />
                    </Carousel.Slide>
                ))}
            </Carousel>
        </div>
    );
}

function ActivityCard({ activity }: { activity: DisplayItem }) {
    const Wrapper = activity.href ? "a" : "div";

    return (
        <Wrapper href={activity.href} className={styles.card}>
            <div className={styles.imageWrap}>
                {activity.imageUrl ? (
                    <img
                        src={activity.imageUrl}
                        alt={activity.label}
                        className={styles.image}
                    />
                ) : (
                    <div className={styles.placeholder}>✦</div>
                )}
            </div>

            <div className={styles.cardBody}>
                <h4>{activity.label}</h4>
                <p>{activity.description}</p>

                <div className={styles.meta}>
                    {activity.durationMin ? (
                        <span className={styles.metaChip}>{activity.durationMin} min</span>
                    ) : null}

                    {activity.ageText ? (
                        <span className={styles.metaChip}>{activity.ageText}</span>
                    ) : null}
                </div>

                {activity.href ? <span className={styles.link}>Découvrir</span> : null}
            </div>
        </Wrapper>
    );
}